import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  TaskAlt as CloseJobIcon,
  VisibilityOutlined as ViewIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import {
  fulfillmentApiBase,
  fulfillmentListPage,
  withAccountBody,
  withAccountParams,
} from '../../utils/fulfillmentApi.mjs';
import ConfirmDialog from './ConfirmDialog';
import FulfillmentStatusChip from './FulfillmentStatusChip';
import FulfillmentProductThumb from './FulfillmentProductThumb';

const PAGE_SIZE_KEY = 'fulfillment_rows_per_page';
const TABLE_SX = {
  minWidth: 760,
  '& .MuiTableCell-body': { py: 0.625 },
  '& .MuiChip-root': { height: 22 },
  '& .MuiChip-label': { px: 0.875 },
};

const COLUMNS = [
  { key: '__view', label: '' },
  { key: 'code', label: 'Задание', sortable: true },
  { key: 'progress', label: 'Прогресс' },
  { key: 'assigned_to', label: 'Сборщик', sortable: true },
  { key: 'status', label: 'Статус', sortable: true },
  { key: '__actions', label: '', align: 'right' },
];

function initialPageSize() {
  const saved = Number(localStorage.getItem(PAGE_SIZE_KEY));
  return [25, 50, 100, 200].includes(saved) ? saved : 50;
}

function errorText(error) {
  return error?.response?.data?.message || error.message || 'Не удалось загрузить задания';
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

/** Закрытие доступно для new/in_progress, в т.ч. с 0 подобранных позиций. */
function canCloseJob(job) {
  return job && !['completed', 'cancelled'].includes(job.status);
}

function needsMoveRetry(job) {
  return Boolean(job?.ms_move_required && job?.ms_move_status === 'error' && job?.ms_move_error);
}

export default function PickingJobsPanel({
  accountId,
  marketplace = 'wb',
  canManage,
  refreshKey = 0,
  onChanged,
}) {
  const isOzon = marketplace === 'ozon';
  const canAssign = marketplace === 'wb';
  const apiBase = fulfillmentApiBase(marketplace);
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialPageSize);
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [assigning, setAssigning] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get(`${apiBase}/picking-jobs`, {
        params: withAccountParams(marketplace, accountId, {
          page: fulfillmentListPage(marketplace, page),
          page_size: rowsPerPage,
          sort_key: sort.key,
          sort_dir: sort.dir,
        }),
      });
      setJobs(data?.items || []);
      setTotal(Number(data?.total) || 0);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setLoading(false);
    }
  }, [accountId, apiBase, marketplace, page, rowsPerPage, sort]);

  useEffect(() => { setPage(0); }, [accountId, marketplace]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    // Список исполнителей нужен для create/assign; assign UI остаётся WB-only ниже.
    if (!canManage) {
      setAssignees([]);
      return undefined;
    }
    let cancelled = false;
    axios.get(`${apiBase}/picking-jobs/assignees`, {
      params: {
        account_id: accountId || undefined,
        marketplace: marketplace || undefined,
      },
      silent: true,
    })
      .then(({ data }) => {
        if (!cancelled) setAssignees(data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setAssignees([]);
      });
    return () => { cancelled = true; };
  }, [apiBase, canManage, accountId, marketplace]);

  const handleSort = (key) => {
    setPage(0);
    setSort((old) => ({
      key,
      dir: old.key === key && old.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  const jobDetailUrl = (id) => `${apiBase}/picking-jobs/${id}`;

  const openDetail = async (job) => {
    setDetail(job);
    setDetailLoading(true);
    setError('');
    try {
      const { data } = await axios.get(jobDetailUrl(job.id), {
        params: withAccountParams(marketplace, accountId),
      });
      setDetail(data);
    } catch (requestError) {
      setError(errorText(requestError));
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const cancel = async (job) => {
    setAction(`cancel:${job.id}`);
    try {
      await axios.post(
        `${apiBase}/picking-jobs/${job.id}/cancel`,
        withAccountBody(marketplace, accountId),
      );
      setNotice('Задание отменено');
      await load();
      onChanged?.();
      setDetail(null);
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setAction('');
    }
  };

  const closeJob = async (job, { retry = false } = {}) => {
    setAction(`close:${job.id}`);
    setError('');
    try {
      const { data } = await axios.post(
        `${apiBase}/picking-jobs/${job.id}/close`,
        withAccountBody(marketplace, accountId),
      );
      setNotice(retry
        ? (data?.ms_move_id
          ? `Перемещение проведено, задание ${data.code || job.code || ''} закрыто`
          : 'Задание закрыто')
        : `Задание ${data?.code || job.code || ''} закрыто`);
      await load();
      onChanged?.();
      if (detail && String(detail.id) === String(job.id)) {
        const refreshed = await axios.get(jobDetailUrl(job.id), {
          params: withAccountParams(marketplace, accountId),
        });
        setDetail(refreshed.data);
      }
    } catch (requestError) {
      setError(errorText(requestError));
      if (detail && String(detail.id) === String(job.id)) {
        try {
          const refreshed = await axios.get(jobDetailUrl(job.id), {
            params: withAccountParams(marketplace, accountId),
          });
          setDetail(refreshed.data);
        } catch (_) { /* ignore */ }
      }
      await load();
    } finally {
      setAction('');
    }
  };

  const unpickedCount = (job) => {
    const totalItems = Number(job?.items_total);
    const picked = Number(job?.items_picked);
    if (Number.isFinite(totalItems) && Number.isFinite(picked)) {
      return Math.max(0, totalItems - picked);
    }
    if (Array.isArray(job?.items)) {
      return job.items.filter((item) => item.status !== 'picked').length;
    }
    return 0;
  };

  const askCancelJob = (job) => {
    const code = job?.code || job?.id || '';
    setConfirm({
      title: 'Отменить задание',
      message: `Отменить задание ${code}? Все заказы вернутся в «Ожидают сборки».`,
      confirmText: 'Отменить задание',
      onConfirm: () => {
        setConfirm(null);
        cancel(job);
      },
    });
  };

  const askCloseJob = (job, options) => {
    const code = job?.code || job?.id || '';
    const left = unpickedCount(job);
    const message = left > 0
      ? `Закрыть задание ${code}? Не подобрано ${left} позиций — они вернутся в «Ожидают сборки».`
      : `Закрыть задание ${code}?`;
    setConfirm({
      title: 'Закрыть задание',
      message,
      confirmText: 'Закрыть',
      onConfirm: () => {
        setConfirm(null);
        closeJob(job, options);
      },
    });
  };

  const assign = async (assignedTo) => {
    if (!detail || marketplace !== 'wb') return;
    setAssigning(true);
    try {
      const { data } = await axios.patch(
        `${apiBase}/picking-jobs/${detail.id}/assign`,
        withAccountBody(marketplace, accountId || detail.account_id, {
          assigned_to: Number(assignedTo),
        }),
      );
      setDetail((old) => ({ ...old, assigned_to: data.assigned_to, assignee: data.assignee }));
      await load();
    } catch (requestError) {
      setError(errorText(requestError));
    } finally {
      setAssigning(false);
    }
  };

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice('')}>{notice}</Alert>}

      <Stack direction="row" justifyContent="flex-end">
        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Обновить
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 330px)' }}>
          <Table size="small" stickyHeader sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((column) => (
                  <TableCell key={column.key} align={column.align || 'left'}>
                    {column.sortable ? (
                      <TableSortLabel
                        active={sort.key === column.key}
                        direction={sort.key === column.key ? sort.dir : 'asc'}
                        onClick={() => handleSort(column.key)}
                      >
                        {column.label}
                      </TableSortLabel>
                    ) : column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {jobs.map((job) => {
                const totalItems = Number(job.items_total) || 0;
                const picked = Number(job.items_picked) || 0;
                const progress = totalItems ? Math.round((picked / totalItems) * 100) : 0;
                const closing = action === `close:${job.id}`;
                const retry = needsMoveRetry(job);
                return (
                  <TableRow key={job.id} hover>
                    <TableCell sx={{ width: 44, pr: 0 }}>
                      <Tooltip title="Открыть задание">
                        <IconButton
                          size="small"
                          aria-label="Открыть задание"
                          onClick={() => openDetail(job)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="titleMedium" fontWeight={700}>
                        {job.code || job.name || `ID ${job.id}`}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="p">
                        {job.code ? (job.name || `ID ${job.id}`) : `ID ${job.id}`}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 160 }}>
                      <Typography variant="bodyMedium" fontWeight={700}>
                        {picked} из {totalItems}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ mt: 0.75, height: 6, borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="bodyMedium" fontWeight={700}>
                        {job.assignee?.name || 'Не назначен'}
                      </Typography>
                      {job.created_at && (
                        <Typography variant="caption" color="text.secondary" component="p">
                          Создано: {formatDate(job.created_at)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <FulfillmentStatusChip kind="pickingJob" status={job.status} />
                      {retry && (
                        <Typography variant="caption" color="warning.main" component="p" sx={{ mt: 0.5 }}>
                          Ошибка перемещения МС
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {canManage && canCloseJob(job) && (
                        <Tooltip title={retry ? 'Повторить закрытие' : 'Закрыть задание'}>
                          <span>
                            <IconButton
                              color={retry ? 'warning' : 'primary'}
                              disabled={Boolean(action)}
                              onClick={() => askCloseJob(job, { retry })}
                              aria-label={retry ? 'Повторить закрытие' : 'Закрыть задание'}
                            >
                              {closing
                                ? <CircularProgress size={20} color="inherit" />
                                : (retry ? <ReplayIcon /> : <CloseJobIcon />)}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {!jobs.length && (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 6 }}>
                    Заданий пока нет. Создайте задание из выбранных заказов на вкладке «Ожидают сборки».
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}
        <TablePagination
          component="div"
          count={total}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_event, value) => setPage(value)}
          onRowsPerPageChange={(event) => {
            const value = Number(event.target.value);
            localStorage.setItem(PAGE_SIZE_KEY, String(value));
            setRowsPerPage(value);
            setPage(0);
          }}
          rowsPerPageOptions={[25, 50, 100, 200]}
          labelRowsPerPage="На странице:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} из ${count}`}
        />
      </Paper>

      <Dialog open={Boolean(detail)} onClose={() => setDetail(null)} fullWidth maxWidth="md">
        <DialogTitle>
          <Stack spacing={0.5}>
            <Typography variant="titleLarge">{detail?.code || detail?.name || 'Задание на подбор'}</Typography>
            {detail?.code && (
              <Typography variant="bodySmall" color="text.secondary">{detail.name}</Typography>
            )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Stack spacing={1.25}>
              {needsMoveRetry(detail) && (
                <Alert severity="error">
                  Перемещение МойСклад не выполнено: {detail.ms_move_error}. Можно безопасно повторить закрытие —
                  повторное перемещение не создастся.
                </Alert>
              )}
              {detail?.ms_move_status === 'done' && detail?.ms_move_name && (
                <Alert severity="success">Перемещение МС: {detail.ms_move_name}</Alert>
              )}
              {canManage && canAssign && detail && !['completed', 'cancelled'].includes(detail.status) && (
                <FormControl fullWidth size="small">
                  <InputLabel>Назначенный сборщик</InputLabel>
                  <Select
                    value={detail.assigned_to || ''}
                    label="Назначенный сборщик"
                    disabled={assigning}
                    onChange={(event) => assign(event.target.value)}
                  >
                    {assignees.map((assignee) => (
                      <MenuItem key={assignee.id} value={assignee.id}>{assignee.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {(detail?.items || []).map((item) => {
                const article = item.product?.article
                  || item.task?.article
                  || item.posting?.offer_id
                  || item.posting?.sku
                  || (item.task?.nm_id ? `nmID ${item.task.nm_id}` : null)
                  || '—';
                const orderLabel = item.posting?.yandex_order_id
                  || item.yandex_order_id
                  || item.posting?.posting_number
                  || item.task?.wb_order_id
                  || item.posting_number
                  || '—';
                const barcode = item.posting?.barcode || item.task?.barcode || '—';
                return (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" gap={1} alignItems={{ sm: 'center' }}>
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                        <FulfillmentProductThumb
                          product={item.product}
                          task={item.task || item.posting}
                          size={48}
                        />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="bodyMedium" fontWeight={700}>
                            {article}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" component="p">
                            {isOzon ? 'Отправление' : 'Заказ'}
                            {' '}
                            {orderLabel}
                            {' · ШК '}
                            {barcode}
                            {marketplace === 'wb' && item.task?.nm_id ? ` · nmID ${item.task.nm_id}` : ''}
                          </Typography>
                          {item.stock && (
                            <Typography variant="caption" color="text.secondary" component="p">
                              МС {item.stock.ms_stock ?? '—'} · резерв {item.stock.reserved_total ?? 0} · доступно {item.stock.available ?? '—'}
                            </Typography>
                          )}
                          {item.posting?.composition_alert && (
                            <Typography variant="caption" color="warning.main" component="p">
                              Изменился состав отправления
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      <FulfillmentStatusChip
                        code={item.status === 'picked' ? 'picked' : 'pending'}
                        label={item.status === 'picked' ? 'Подобран' : 'Ожидает'}
                      />
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {canManage && detail?.status === 'new' && (
            <Button color="error" onClick={() => askCancelJob(detail)} disabled={Boolean(action)}>
              Отменить задание
            </Button>
          )}
          {canManage && canCloseJob(detail) && (
            <Button
              color={needsMoveRetry(detail) ? 'warning' : 'primary'}
              variant="contained"
              disabled={Boolean(action)}
              onClick={() => askCloseJob(detail, { retry: needsMoveRetry(detail) })}
            >
              {action === `close:${detail?.id}`
                ? <CircularProgress size={18} color="inherit" />
                : (needsMoveRetry(detail) ? 'Повторить закрытие' : 'Закрыть задание')}
            </Button>
          )}
          <Button onClick={() => setDetail(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirm)}
        title={confirm?.title}
        message={confirm?.message}
        confirmText={confirm?.confirmText}
        onClose={() => setConfirm(null)}
        onConfirm={confirm?.onConfirm}
      />
    </Stack>
  );
}
